import React, { useContext, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ConfirmDeleteDialog from "../../ConfirmDeleteDialog";
import { UserContext } from "../../../context";
import { MemberColumn } from "./HubMemberSelectionDialog";

export default function EditGroupDialog(props) {
    const { currentUserData } = useContext(UserContext);
    const [selectedFilter, setSelectedFilter] = useState("");
    const [availableFilter, setAvailableFilter] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [otherUsers, setOtherUsers] = useState([]);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);
    const [groupName, setGroupName] = useState("");

    useEffect(() => {
        if (!props.open) {
            return;
        }

        const selectedArray = [];
        const otherArray = [];

        props.hubUsers.forEach((user) => {
            if (
                (props.group.users || []).some(
                    (groupUser) => groupUser.key === user.key,
                )
            ) {
                selectedArray.push(user);
            } else {
                otherArray.push(user);
            }
        });

        setSelectedUsers(selectedArray);
        setOtherUsers(otherArray);
        setGroupName(props.group.name);
        setSelectedFilter("");
        setAvailableFilter("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    const sortedUsers = (users) =>
        [...users].sort((a, b) =>
            (a.lastName || a.fullName || "").localeCompare(
                b.lastName || b.fullName || "",
                undefined,
                { sensitivity: "base" },
            ),
        );

    const usersMatching = (users, filter) => {
        const searchTerm = filter.trim().toLocaleLowerCase();
        return sortedUsers(users).filter((user) =>
            (user.fullName || "")
                .toLocaleLowerCase()
                .includes(searchTerm),
        );
    };

    const visibleSelectedUsers = usersMatching(
        selectedUsers,
        selectedFilter,
    );
    const visibleAvailableUsers = usersMatching(otherUsers, availableFilter);

    function handleUserSelect(user) {
        setSelectedUsers((currentUsers) => [...currentUsers, user]);
        setOtherUsers((currentUsers) =>
            currentUsers.filter((otherUser) => otherUser.key !== user.key),
        );
    }

    function handleDeleteUser(user) {
        setSelectedUsers((currentUsers) =>
            currentUsers.filter((selectedUser) => selectedUser.key !== user.key),
        );
        setOtherUsers((currentUsers) => [...currentUsers, user]);
    }

    function handleCancel() {
        props.onClose();
    }

    function handleApply() {
        if (groupName !== "") {
            props.onClose(selectedUsers, {
                ...props.group,
                name: groupName,
            });
        }
    }

    function handleCloseOnDeleteDialog(result) {
        if (result === true && currentUserData) {
            props.onDelete(props.group);
        }
        setConfirmDeleteDialogOpen(false);
    }

    return (
        <>
            <Dialog
                onClose={handleCancel}
                open={props.open}
                fullWidth
                maxWidth="lg"
                aria-labelledby="edit-group-dialog-title"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            maxHeight: "92vh",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <GroupsOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="edit-group-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Edit group
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Update the group name and members. These changes
                            will not be saved until you save the hub.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close group editor"
                        onClick={handleCancel}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 1.5, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            mb: 2,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <TextField
                            label="Group name"
                            value={groupName}
                            onChange={(event) =>
                                setGroupName(event.target.value)
                            }
                            fullWidth
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                md: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 2,
                        }}
                    >
                        <MemberColumn
                            title="Selected"
                            users={visibleSelectedUsers}
                            noun="user"
                            pluralNoun="users"
                            filter={selectedFilter}
                            onFilterChange={setSelectedFilter}
                            emptyMessage="No users are selected."
                            selectedColumn
                            totalCount={selectedUsers.length}
                            onAdd={handleUserSelect}
                            onRemove={handleDeleteUser}
                        />
                        <MemberColumn
                            title="Available"
                            users={visibleAvailableUsers}
                            noun="user"
                            pluralNoun="users"
                            filter={availableFilter}
                            onFilterChange={setAvailableFilter}
                            emptyMessage="There are no more users available."
                            selectedColumn={false}
                            totalCount={otherUsers.length}
                            onAdd={handleUserSelect}
                            onRemove={handleDeleteUser}
                        />
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setConfirmDeleteDialogOpen(true)}
                    >
                        Delete group
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button color="inherit" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={handleApply}
                            disabled={groupName === ""}
                        >
                            Apply to draft
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            {confirmDeleteDialogOpen && (
                <ConfirmDeleteDialog
                    title="Delete Group"
                    message="Are you sure you want to delete this group?"
                    supportingMessage="The group will not be deleted until you save the hub."
                    open={confirmDeleteDialogOpen}
                    onClose={handleCloseOnDeleteDialog}
                />
            )}
        </>
    );
}
